import React, { useEffect, useRef, useState } from 'react';
import { Alert, InputNumber, Modal, Radio, Typography } from 'antd';
import { smartBetaApi, SmartBetaView, SmartBetaWithdrawResult } from '../../../services/smartBetaApi';
import { formatNumber, numberInputProps } from '../../../utils/numberFormat';
import { newIdempotencyKey } from '../../../utils/idempotencyKey';
import { useIsMobile } from '../../../hooks/useIsMobile';

const { Text } = Typography;

interface Props {
  open: boolean;
  config: SmartBetaView;
  onCancel: () => void;
  onDone: (result: SmartBetaWithdrawResult) => void;
}

const SmartBetaWithdrawModal: React.FC<Props> = ({ open, config, onCancel, onDone }) => {
  const isMobile = useIsMobile();
  const [amount, setAmount] = useState<number | null>(null);
  const [sellNow, setSellNow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per intent; kept after a timeout or a part-way stop so a retry finishes it.
  const keyRef = useRef(newIdempotencyKey());
  const partialRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setAmount(null);
    setSellNow(false);
    setError(null);
    keyRef.current = newIdempotencyKey();
    partialRef.current = false;
  }, [open]);

  const idle = Math.max(Number(config.idleCash) || 0, 0);
  const pending = Number(config.pendingWithdrawal) || 0;
  const now = Math.min(amount ?? 0, idle);
  const toSell = Math.max((amount ?? 0) - idle, 0);

  const submit = async () => {
    if (!amount || amount <= 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      onDone(await smartBetaApi.withdraw(config.dataConfigId, amount, keyRef.current, toSell > 0 && sellNow));
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { message?: string } }; message?: string };
      const msg = err.response?.data?.message ?? err.message ?? String(e);
      partialRef.current = /withdraw stopped after/.test(msg);
      if (err.response?.status != null && !partialRef.current) keyRef.current = newIdempotencyKey();
      setError(partialRef.current ? `${msg} Press Withdraw again with the same amount to finish.` : msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Withdraw from Smart Beta"
      open={open}
      onCancel={onCancel}
      onOk={submit}
      okText="Withdraw"
      okButtonProps={{
        loading: submitting,
        disabled: !amount,
        style: { background: '#F6465D', borderColor: '#F6465D' },
      }}
      width={isMobile ? '100%' : 500}
      style={isMobile ? { top: 'auto', bottom: 0, margin: 0, maxWidth: '100vw', paddingBottom: 0 } : undefined}
      destroyOnClose
    >
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Capital:
        </Text>{' '}
        <Text strong>{formatNumber(Number(config.totalCapital))} VND</Text>
        <br />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Idle cash (paid at once):
        </Text>{' '}
        <Text strong>{formatNumber(idle)} VND</Text>
        {pending > 0 && (
          <>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Already waiting to be sold for:
            </Text>{' '}
            <Text strong style={{ color: '#F0B90B' }}>
              {formatNumber(pending)} VND
            </Text>
          </>
        )}
      </div>
      <InputNumber
        {...numberInputProps()}
        min={1}
        precision={0}
        value={amount}
        onChange={(v) => {
          setAmount(v);
          if (!partialRef.current) keyRef.current = newIdempotencyKey();
        }}
        onPressEnter={submit}
        addonAfter="VND"
        placeholder="Amount"
        style={{ width: '100%' }}
        autoFocus
        aria-label="Withdraw amount"
      />
      {amount != null && amount > 0 && (
        <div style={{ marginTop: 12, fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Paid now</span>
            <span>{formatNumber(now)} VND</span>
          </div>
          {toSell > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Raised by selling, then paid</span>
                <span>{formatNumber(toSell)} VND</span>
              </div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', margin: '8px 0 4px' }}>
                Every ticker sells its share. When:
              </Text>
              <Radio.Group value={sellNow} onChange={(e) => setSellNow(e.target.value)}>
                <Radio value={false}>
                  At the next rebalance{config.nextRebalance ? ` (${config.nextRebalance})` : ''}
                </Radio>
                <Radio value>Now (starts the bot)</Radio>
              </Radio.Group>
            </>
          )}
        </div>
      )}
      {error && <Alert type="error" showIcon message={error} style={{ marginTop: 12 }} />}
    </Modal>
  );
};

export default SmartBetaWithdrawModal;
